import type { FC } from 'react';
import React, { memo, useCallback, useState, useMemo } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import DropdownMenu, { DropdownItemGroup as AKDropdownItemGroup } from '@atlaskit/dropdown-menu';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import AkTooltip from '@atlaskit/tooltip/Tooltip';
import AkButton from '@atlaskit/button/standard-button';

import { fg } from '@confluence/feature-gating';

import { SortOrders } from './SortOrders.ts';
import { LoadingSortByDropdown } from './LoadingSortByDropdown.tsx';
import { SortByDropdownClickItem } from './SortByDropDownClickItem.tsx';
import { SortByLabel, SortByDropdownWrapper, DropdownWrapper } from './styles.tsx';

export type SortByDropdownProps = {
	onChange: (sortBy: SortOrders) => void;
	value?: SortOrders;
	isDisabled?: boolean;
	showLabel?: boolean;
	analyticsSource: string;
};

export const i18n = defineMessages({
	sortByLabel: {
		id: 'template-utils.sort-by-dropdown.sortBy.label',
		defaultMessage: 'Sort by',
		description: 'label shown next to the sort order dropdown for in editor template drawer',
	},
	recommendedLabel: {
		id: 'template-utils.sort-by-dropdown.sortBy.option.recommended.label',
		defaultMessage: 'Recommended',
		description: 'sort order option shown in a dropdown item for in editor template drawer',
	},
	alphabeticalLabel: {
		id: 'template-utils.sort-by-dropdown.sortBy.option.alphabetical.label',
		defaultMessage: 'Alphabetical',
		description: 'sort order option shown in a dropdown item for in editor template drawer',
	},
	sortByRecommendedTooltip: {
		id: 'template-utils.sort-by-dropdown.sortBy.recommended.tooltip.content',
		defaultMessage: 'Recommended templates are sorted by frequency of use',
		description:
			'tooltip shown when hovering over the sort order dropdown for in editor template drawer when recommendation is selected',
	},
});

export const SortByDropdownComponent: FC<SortByDropdownProps> = ({
	onChange,
	value,
	isDisabled = false,
	showLabel = true,
	analyticsSource,
}) => {
	const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const options = {
		[SortOrders.RELEVANCY_SORT]: (
			<FormattedMessage {...i18n.recommendedLabel} key={i18n.recommendedLabel.id} />
		),
		[SortOrders.ALPHABETICAL_SORT]: (
			<FormattedMessage {...i18n.alphabeticalLabel} key={i18n.alphabeticalLabel.id} />
		),
	};

	const handleOpenChange = useCallback(
		({ isOpen }: any) => {
			setIsMenuOpen(isOpen);

			if (isOpen) {
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						source: analyticsSource,
						action: 'clicked',
						actionSubject: 'dropdown',
						actionSubjectId: 'sortingOrderDropdown',
					},
				}).fire();
			}
		},
		[setIsMenuOpen, analyticsSource, createAnalyticsEvent],
	);

	const handleSortOrderChange = useMemo(
		() => (selectedSortOption: SortOrders) => () => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: analyticsSource,
					action: 'clicked',
					actionSubject: 'dropdownItem',
					actionSubjectId: 'sortingOrderDropdownItem',
					attributes: {
						previousSortingScheme: value,
						newSortingScheme: selectedSortOption,
					},
				},
			}).fire();
			onChange(selectedSortOption);
		},
		[analyticsSource, onChange, createAnalyticsEvent, value],
	);

	if (!value) {
		return <LoadingSortByDropdown />;
	}

	const isSortByRecommendedSelected = value === SortOrders.RELEVANCY_SORT;

	return (
		<SortByDropdownWrapper data-testid="templates-sort-by-dropdown">
			{showLabel && (
				<SortByLabel>
					<FormattedMessage {...i18n.sortByLabel} />
				</SortByLabel>
			)}
			<DropdownWrapper>
				<DropdownMenu
					appearance="tall"
					trigger={({ triggerRef, ...props }) => (
						<AkTooltip
							content={
								isSortByRecommendedSelected && !isMenuOpen ? (
									<FormattedMessage {...i18n.sortByRecommendedTooltip} />
								) : (
									''
								)
							}
							testId="templates-sort-by-dropdown-tooltip"
						>
							<AkButton
								{...props}
								appearance="subtle"
								isDisabled={isDisabled}
								id="templates-sort-by-order"
								shouldFitContainer
								// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
								style={{ textAlign: 'left' }}
								isSelected={isMenuOpen}
								iconAfter={<ChevronDownIcon label="" size="small" />}
								testId="templates-sort-by-order-button"
								ref={triggerRef}
							>
								{options[value]}
							</AkButton>
						</AkTooltip>
					)}
					isOpen={isMenuOpen}
					onOpenChange={handleOpenChange}
					placement="bottom-start"
					shouldFlip={false}
					testId="templates-sort-by-order"
					shouldRenderToParent={fg('should-render-to-parent-should-be-truetailored-exp')}
				>
					<AKDropdownItemGroup>
						<SortByDropdownClickItem
							msg={i18n.recommendedLabel}
							onClick={handleSortOrderChange(SortOrders.RELEVANCY_SORT)}
						/>
						<SortByDropdownClickItem
							msg={i18n.alphabeticalLabel}
							onClick={handleSortOrderChange(SortOrders.ALPHABETICAL_SORT)}
						/>
					</AKDropdownItemGroup>
				</DropdownMenu>
			</DropdownWrapper>
		</SortByDropdownWrapper>
	);
};

export const SortByDropdown = memo(SortByDropdownComponent);
